import { memo, useId, useMemo } from "react";
import { motion, useReducedMotion } from "framer-motion";

export const TINTS = {
  clear: { label: "Clear", rgb: "205, 228, 242" },
  pink: { label: "Pink", rgb: "255, 140, 190" },
  blue: { label: "Blue", rgb: "110, 170, 255" },
  green: { label: "Green", rgb: "100, 215, 155" },
  purple: { label: "Purple", rgb: "175, 135, 255" },
  gold: { label: "Gold", rgb: "255, 200, 80" },
};
export const ACCESSORIES = { none: "None", party_hat: "Party hat", sunglasses: "Sunglasses", bow: "Bow", crown: "Crown", headphones: "Headphones" };
export const MAX_COINS = 120;
export const coinCount = (bal) => Math.max(0, Math.min(MAX_COINS, Math.floor(bal + 1e-9)));
export const SLOT = { x: 205, y: 78 };

// Deterministic pile: coins fill the belly bottom-up, mounded in the middle.
const PILE = (() => {
  let seed = 7;
  const rnd = () => ((seed = (seed * 16807) % 2147483647) / 2147483647);
  const cx = 200, cy = 180, rx = 120, ry = 86;
  const spots = [];
  for (let row = 0; row < 26; row++) {
    const y = 252 - row * 6.2;
    const dy = (y - cy) / ry;
    if (Math.abs(dy) >= 1) continue;
    const half = rx * Math.sqrt(1 - dy * dy) - 14;
    const off = row % 2 ? 10.5 : 0;
    for (let x = cx - half + off; x <= cx + half; x += 21) {
      spots.push({ x: x + (rnd() - 0.5) * 6, y: y + (rnd() - 0.5) * 3, r: (rnd() - 0.5) * 22, score: row + Math.abs(x - cx) / 24 + rnd() * 0.8 });
    }
  }
  return spots.sort((a, b) => a.score - b.score).slice(0, MAX_COINS).sort((a, b) => b.y - a.y);
})();
const ORDER = PILE.map((p, i) => ({ ...p, i }));
export const spotOf = (idx) => ORDER[Math.min(idx, ORDER.length - 1)];

function Coin({ x, y, r, g }) {
  return (
    <g transform={`translate(${x} ${y}) rotate(${r})`}>
      <ellipse cx="0" cy="2.6" rx="12" ry="5.4" fill="#9a6b12" />
      <ellipse cx="0" cy="0" rx="12" ry="5.4" fill={`url(#${g}-coin)`} stroke="#b9861d" strokeWidth="0.8" />
      <ellipse cx="0" cy="0" rx="7.6" ry="3.2" fill="none" stroke="#fff3c4" strokeOpacity="0.7" strokeWidth="0.8" />
      <ellipse cx="-4" cy="-1.6" rx="3" ry="1" fill="#fffbe8" opacity="0.85" />
    </g>
  );
}

const Pile = memo(function Pile({ count, g }) {
  // draw back-to-front so higher coins overlap lower ones correctly
  const coins = ORDER.slice(0, count).sort((a, b) => a.y - b.y || a.i - b.i);
  return coins.map((c) => <Coin key={c.i} {...c} g={g} />);
});

function Accessory({ kind }) {
  switch (kind) {
    case "party_hat":
      return (
        <g data-testid="piggy-accessory-party-hat">
          <path d="M262 98 L318 92 L282 26 Z" fill="#ff5fa2" />
          <path d="M270 82 L310 78 M276 64 L302 61 M281 46 L294 44" stroke="#ffe066" strokeWidth="6" strokeLinecap="round" />
          <circle cx="282" cy="25" r="8" fill="#ffe066" />
        </g>
      );
    case "sunglasses":
      return (
        <g data-testid="piggy-accessory-sunglasses">
          <path d="M282 140 L250 124" stroke="#111" strokeWidth="4" strokeLinecap="round" />
          <rect x="282" y="130" width="38" height="24" rx="10" fill="#151515" />
          <path d="M320 140 L334 146" stroke="#111" strokeWidth="4" strokeLinecap="round" />
          <path d="M288 136 L300 136" stroke="#fff" strokeOpacity="0.55" strokeWidth="3" strokeLinecap="round" />
        </g>
      );
    case "bow":
      return (
        <g data-testid="piggy-accessory-bow">
          <path d="M262 84 L236 66 L238 102 Z" fill="#ff4f8b" />
          <path d="M262 84 L288 66 L286 102 Z" fill="#ff4f8b" />
          <circle cx="262" cy="84" r="8" fill="#ff8fb6" />
        </g>
      );
    case "crown":
      return (
        <g data-testid="piggy-accessory-crown">
          <path d="M252 98 L250 58 L268 76 L282 48 L296 76 L314 58 L312 98 Z" fill="#ffc83d" stroke="#d99a00" strokeWidth="2" />
          <circle cx="282" cy="84" r="5" fill="#ff4f8b" /><circle cx="264" cy="88" r="3.5" fill="#4fc3ff" /><circle cx="300" cy="88" r="3.5" fill="#6be38f" />
        </g>
      );
    case "headphones":
      return (
        <g data-testid="piggy-accessory-headphones">
          <path d="M240 128 C238 52, 330 44, 334 118" fill="none" stroke="#2b2b33" strokeWidth="9" strokeLinecap="round" />
          <rect x="226" y="112" width="24" height="36" rx="10" fill="#ff6a3d" stroke="#2b2b33" strokeWidth="3" />
          <rect x="322" y="104" width="22" height="32" rx="10" fill="#ff6a3d" stroke="#2b2b33" strokeWidth="3" />
        </g>
      );
    default:
      return null;
  }
}

// flying: { kind: "in" | "out", from: coin index, k: count, key }
function Flying({ flying, g, reduce }) {
  if (!flying) return null;
  const list = Array.from({ length: flying.k }, (_, n) => ({ n, spot: spotOf(flying.from + n) }));
  if (reduce) {
    return list.map(({ n, spot }) => (
      <motion.g key={`${flying.key}-${n}`} initial={{ opacity: flying.kind === "in" ? 0 : 1 }} animate={{ opacity: flying.kind === "in" ? 1 : 0 }} transition={{ duration: 0.5 }}>
        <Coin {...spot} g={g} />
      </motion.g>
    ));
  }
  return list.map(({ n, spot }) => {
    const delay = n * 0.09;
    if (flying.kind === "in") {
      return (
        <motion.g key={`${flying.key}-${n}`} initial={{ x: SLOT.x - spot.x, y: SLOT.y + 14 - spot.y, opacity: 0 }}
          animate={{ x: 0, y: 0, opacity: 1 }} transition={{ delay: delay + 0.18, y: { type: "spring", stiffness: 260, damping: 13, delay: delay + 0.18 }, x: { duration: 0.45, delay: delay + 0.18 }, opacity: { duration: 0.05, delay: delay + 0.18 } }}>
          <Coin {...spot} g={g} />
        </motion.g>
      );
    }
    return (
      <motion.g key={`${flying.key}-${n}`} initial={{ x: 0, y: 0 }} animate={{ x: SLOT.x - spot.x, y: SLOT.y + 10 - spot.y, opacity: [1, 1, 0] }} transition={{ delay, duration: 0.42, ease: "easeIn" }}>
        <Coin {...spot} g={g} />
      </motion.g>
    );
  });
}

// Coins seen outside the glass: entering through the slot, or flying out of it.
function SlotCoins({ flying, g, reduce }) {
  if (!flying || reduce) return null;
  return Array.from({ length: flying.k }, (_, n) => {
    const delay = n * 0.09;
    if (flying.kind === "in") {
      return (
        <motion.rect key={`s${flying.key}-${n}`} x={SLOT.x - 11} width="22" height="6" rx="3" fill={`url(#${g}-coin)`} stroke="#b9861d"
          initial={{ y: -30, opacity: 0 }} animate={{ y: [-30, SLOT.y - 4, SLOT.y + 4], opacity: [1, 1, 0] }} transition={{ delay, duration: 0.22, ease: "easeIn" }} />
      );
    }
    const dir = n % 2 ? 1 : -1;
    return (
      <motion.g key={`s${flying.key}-${n}`} initial={{ x: SLOT.x, y: SLOT.y, opacity: 0, rotate: 0 }}
        animate={{ x: SLOT.x + dir * (70 + (n % 4) * 35), y: [SLOT.y, SLOT.y - 60 - (n % 3) * 20, 340], opacity: [0, 1, 1, 0], rotate: dir * 540 }}
        transition={{ delay: delay + 0.4, duration: 0.95, ease: "easeOut" }}>
        <circle r="11" fill={`url(#${g}-coin)`} stroke="#b9861d" strokeWidth="1.2" />
        <circle r="6.5" fill="none" stroke="#fff3c4" strokeOpacity="0.7" />
      </motion.g>
    );
  });
}

export default function GlassPiggy({ balance = 0, count, tint = "clear", accessory = "none", flying = null, wiggle = 0, size = "lg", onTap, testid = "glass-piggy" }) {
  const g = useId().replace(/:/g, "");
  const reduce = useReducedMotion();
  const rgb = TINTS[tint]?.rgb || TINTS.clear.rgb;
  const shown = count ?? coinCount(balance);
  const full = balance >= MAX_COINS;
  const s = 1 + 0.15 * Math.min(Math.max(balance, 0), 100) / 100;
  const width = { lg: "min(100%, 460px)", md: "220px", sm: "120px" }[size];
  const wiggleAnim = reduce ? { opacity: [1, 0.75, 1] } : { rotate: [0, -7, 6, -4, 3, 0], y: [0, -8, 0, -3, 0, 0] };

  const body = useMemo(() => "M70 175 C70 105 135 72 210 74 C268 75 300 100 318 128 C350 134 362 160 360 182 C358 204 342 222 318 226 C298 258 255 278 205 278 C125 278 70 240 70 175 Z", []);

  return (
    <div className="relative select-none" style={{ width }} data-testid={testid} data-coins={shown} data-tint={tint} data-accessory={accessory} data-full={full ? "1" : "0"} data-scale={s.toFixed(3)}>
      <motion.svg viewBox="0 0 400 320" className="block h-auto w-full overflow-visible" role={onTap ? "button" : "img"} aria-label={`Glass piggy bank with ${shown} coins`}
        onClick={onTap} style={{ cursor: onTap ? "pointer" : "default", filter: full ? "drop-shadow(0 0 26px rgba(255,196,60,0.75))" : "none" }}
        key={wiggle} animate={wiggle ? wiggleAnim : undefined} transition={{ duration: 0.7 }}>
        <defs>
          <radialGradient id={`${g}-coin`} cx="35%" cy="30%" r="80%"><stop offset="0" stopColor="#fff2a8" /><stop offset="0.45" stopColor="#f7c637" /><stop offset="1" stopColor="#c8901a" /></radialGradient>
          <radialGradient id={`${g}-glass`} cx="40%" cy="35%" r="75%"><stop offset="0" stopColor={`rgb(${rgb})`} stopOpacity="0.08" /><stop offset="0.7" stopColor={`rgb(${rgb})`} stopOpacity="0.22" /><stop offset="1" stopColor={`rgb(${rgb})`} stopOpacity="0.5" /></radialGradient>
          <linearGradient id={`${g}-shine`} x1="0" y1="0" x2="1" y2="1"><stop offset="0" stopColor="#fff" stopOpacity="0.55" /><stop offset="0.35" stopColor="#fff" stopOpacity="0.05" /><stop offset="1" stopColor="#fff" stopOpacity="0" /></linearGradient>
          <clipPath id={`${g}-inside`}><ellipse cx="200" cy="180" rx="124" ry="90" /></clipPath>
        </defs>
        <ellipse cx="205" cy="300" rx={130 * s} ry="12" fill="#000" opacity="0.28" />
        <g transform={`translate(205 180) scale(${s} ${1 + (s - 1) * 0.7}) translate(-205 -180)`}>
          {[[118, 248], [160, 258], [245, 258], [285, 246]].map(([x, y]) => (
            <rect key={x} x={x - 15} y={y} width="30" height="40" rx="12" fill={`rgb(${rgb})`} fillOpacity="0.35" stroke={`rgb(${rgb})`} strokeOpacity="0.9" strokeWidth="2.5" />
          ))}
          <path d="M72 168 C48 160 44 136 60 132 C74 128 74 150 58 150" fill="none" stroke={`rgb(${rgb})`} strokeWidth="4" strokeLinecap="round" strokeOpacity="0.9" />
          <path d={body} fill={`url(#${g}-glass)`} />
          <g clipPath={`url(#${g}-inside)`}>
            <Pile count={shown} g={g} />
            <Flying flying={flying} g={g} reduce={reduce} />
          </g>
          <path d={body} fill="none" stroke={`rgb(${rgb})`} strokeOpacity="0.9" strokeWidth="3.5" />
          <path d="M112 128 C130 100 165 88 198 88" fill="none" stroke="#fff" strokeOpacity="0.75" strokeWidth="7" strokeLinecap="round" />
          <path d="M96 160 C96 150 99 143 104 136" fill="none" stroke="#fff" strokeOpacity="0.55" strokeWidth="5" strokeLinecap="round" />
          <path d={body} fill={`url(#${g}-shine)`} opacity="0.6" />
          <ellipse cx="160" cy="236" rx="60" ry="10" fill="#fff" opacity="0.08" />
          <path d="M248 88 L262 50 L292 92 Z" fill={`rgb(${rgb})`} fillOpacity="0.4" stroke={`rgb(${rgb})`} strokeWidth="3" strokeLinejoin="round" />
          <ellipse cx="342" cy="176" rx="22" ry="27" fill={`rgb(${rgb})`} fillOpacity="0.45" stroke={`rgb(${rgb})`} strokeWidth="3" />
          <ellipse cx="336" cy="168" rx="3.5" ry="6" fill="#4a2a35" opacity="0.75" /><ellipse cx="349" cy="170" rx="3.5" ry="6" fill="#4a2a35" opacity="0.75" />
          <ellipse cx="290" cy="186" rx="14" ry="8" fill="#ff7aa8" opacity="0.35" />
          <motion.g key={`eye${wiggle}`} style={{ originX: "300px", originY: "143px" }} animate={wiggle && !reduce ? { scaleY: [1, 0.1, 1, 0.1, 1] } : undefined} transition={{ duration: 0.6 }}>
            <ellipse cx="300" cy="143" rx="7" ry="9" fill="#1f1418" /><circle cx="302.5" cy="139.5" r="2.4" fill="#fff" />
          </motion.g>
          <rect x={SLOT.x - 32} y={SLOT.y - 6} width="64" height="11" rx="5.5" fill="#2a1d22" stroke={`rgb(${rgb})`} strokeWidth="2" />
          <Accessory kind={accessory} />
        </g>
        <SlotCoins flying={flying} g={g} reduce={reduce} />
      </motion.svg>
      {full && <span className="absolute left-1/2 top-1 -translate-x-1/2 rounded-full bg-[#ffc83d] px-2.5 py-0.5 text-[10px] font-black uppercase tracking-[0.16em] text-[#3b2600]" data-testid={`${testid}-full`}>Full!</span>}
      <span className="sr-only" data-testid={`${testid}-coin-count`}>{shown}</span>
    </div>
  );
}
